import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import Webcam from 'react-webcam';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { API_URL } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  cacheEmployees,
  findCachedEmployee,
  queuePunch,
  getQueuedPunches,
  removePunchFromQueue,
  getQueueCount
} from '@/utils/offlineStorage';
import {
  Clock, Globe, ArrowLeft, Camera, Check, RefreshCw,
  LogIn, Coffee, RotateCcw, LogOut, User, AlertCircle,
  Building2, Wifi, WifiOff, CloudUpload
} from 'lucide-react';

const API = API_URL;
const KIOSK_TENANT_KEY = 'punchtm-kiosk-tenant';
const KIOSK_TENANT_NAME_KEY = 'punchtm-kiosk-tenant-name';

// Register Service Worker for Kiosk offline mode only
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/sw.js').catch(() => {});
}

const pageVariants = {
  initial: { opacity: 0, x: 50 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -50 }
};

export default function KioskHome() {
  const { t, language, toggleLanguage } = useLanguage();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState('loading');
  const [code, setCode] = useState('');
  const [employee, setEmployee] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [tenantId, setTenantId] = useState(null);
  const [tenantName, setTenantName] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [setupError, setSetupError] = useState('');
  const [cameraError, setCameraError] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const webcamRef = useRef(null);
  const syncIntervalRef = useRef(null);

  // Online/Offline detection
  useEffect(() => {
    const goOnline = () => { setIsOnline(true); syncQueue(); };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Periodic sync attempt + queue count update
  useEffect(() => {
    const updateCount = async () => {
      try { setPendingCount(await getQueueCount()); } catch {}
    };
    updateCount();
    syncIntervalRef.current = setInterval(() => {
      updateCount();
      if (navigator.onLine) syncQueue();
    }, 15000);
    return () => clearInterval(syncIntervalRef.current);
  }, []);

  // Sync queued punches to server
  const syncQueue = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const queued = await getQueuedPunches();
      let synced = 0;
      for (const punch of queued) {
        try {
          await axios.post(`${API}/punch`, {
            tenantId: punch.tenantId,
            employeeCode: punch.employeeCode,
            eventType: punch.eventType,
            photoBase64: punch.photoBase64
          });
          await removePunchFromQueue(punch.localId);
          synced++;
        } catch (e) {
          if (e.response && e.response.status < 500) {
            await removePunchFromQueue(punch.localId);
            synced++;
          }
        }
      }
      if (synced > 0) {
        toast.success(language === 'fr'
          ? `${synced} pointage(s) synchronise(s)`
          : `${synced} punch(es) synced`
        );
      }
      setPendingCount(await getQueueCount());
    } catch {} finally {
      setSyncing(false);
    }
  };

  // Tenant resolution
  useEffect(() => {
    const initTenant = async () => {
      const urlCode = searchParams.get('t') || searchParams.get('tenant');
      if (urlCode) {
        try {
          const response = await axios.get(`${API}/kiosk/tenant/${urlCode}`);
          const tenant = response.data;
          setTenantId(tenant.id);
          setTenantName(tenant.name);
          localStorage.setItem(KIOSK_TENANT_KEY, tenant.id);
          localStorage.setItem(KIOSK_TENANT_NAME_KEY, tenant.name);
          // Cache employees for offline use
          cacheEmployeeList(tenant.id);
          setStep('code');
          return;
        } catch {
          // Try offline: use saved tenant
          const savedId = localStorage.getItem(KIOSK_TENANT_KEY);
          if (savedId) {
            setTenantId(savedId);
            setTenantName(localStorage.getItem(KIOSK_TENANT_NAME_KEY) || urlCode);
            setStep('code');
            return;
          }
          setStep('setup');
          return;
        }
      }

      const savedId = localStorage.getItem(KIOSK_TENANT_KEY);
      const savedName = localStorage.getItem(KIOSK_TENANT_NAME_KEY);
      if (savedId) {
        try {
          const response = await axios.get(`${API}/kiosk/tenants/${savedId}`);
          setTenantId(savedId);
          setTenantName(response.data.name || savedName);
          cacheEmployeeList(savedId);
          setStep('code');
          return;
        } catch {
          // Offline but have saved tenant
          if (!navigator.onLine) {
            setTenantId(savedId);
            setTenantName(savedName || 'Hors ligne');
            setStep('code');
            return;
          }
          localStorage.removeItem(KIOSK_TENANT_KEY);
          localStorage.removeItem(KIOSK_TENANT_NAME_KEY);
        }
      }
      setStep('setup');
    };
    initTenant();
  }, [searchParams]);

  // Cache employees for offline
  const cacheEmployeeList = async (tid) => {
    try {
      const res = await axios.get(`${API}/kiosk/employees?tenantId=${tid}`);
      if (res.data && res.data.length > 0) {
        await cacheEmployees(tid, res.data);
      }
    } catch {
      // Silently fail - cached data might already exist
    }
  };

  useEffect(() => {
    if (step === 'confirmation' && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (step === 'confirmation' && countdown === 0) {
      resetKiosk();
    }
  }, [step, countdown]);

  const resetKiosk = useCallback(() => {
    setStep('code');
    setCode('');
    setEmployee(null);
    setSelectedAction(null);
    setPhoto(null);
    setCountdown(3);
    setCameraError(false);
  }, []);

  const handleNumPadPress = (value) => {
    if (value === 'clear') setCode('');
    else if (value === 'back') setCode(code.slice(0, -1));
    else if (code.length < 5) setCode(code + value);
  };

  const handleCompanyCodeSubmit = async () => {
    if (!companyCode.trim()) return;
    setSetupError('');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/kiosk/tenant/${companyCode.trim()}`);
      const tenant = response.data;
      setTenantId(tenant.id);
      setTenantName(tenant.name);
      localStorage.setItem(KIOSK_TENANT_KEY, tenant.id);
      localStorage.setItem(KIOSK_TENANT_NAME_KEY, tenant.name);
      cacheEmployeeList(tenant.id);
      setStep('code');
    } catch {
      setSetupError(language === 'fr' ? 'Code entreprise invalide' : 'Invalid company code');
    } finally {
      setLoading(false);
    }
  };

  // Employee lookup: online first, fallback to cache
  const handleContinue = async () => {
    if (code.length !== 5) {
      toast.error(t('invalidCode'));
      return;
    }
    setLoading(true);
    try {
      if (navigator.onLine) {
        const response = await axios.get(`${API}/employees/code/${code}?tenantId=${tenantId}`);
        setEmployee(response.data);
        setStep('action');
      } else {
        throw new Error('offline');
      }
    } catch {
      // Fallback to cached data
      try {
        const cached = await findCachedEmployee(code);
        if (cached) {
          setEmployee(cached);
          setStep('action');
          if (!navigator.onLine) {
            toast.info(language === 'fr' ? 'Mode hors ligne - donnees en cache' : 'Offline mode - cached data');
          }
        } else {
          toast.error(navigator.onLine ? t('employeeNotFound') : (language === 'fr' ? 'Employe non trouve (hors ligne)' : 'Employee not found (offline)'));
          setCode('');
        }
      } catch {
        toast.error(t('employeeNotFound'));
        setCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  const getAvailableActions = () => {
    if (!employee) return [];
    const status = employee.currentStatus;
    const actions = [];
    if (status === 'OUT') {
      actions.push({ type: 'IN', label: t('clockIn'), icon: LogIn, color: 'bg-punch-in' });
    }
    if (status === 'IN') {
      actions.push({ type: 'BREAK_START', label: t('breakStart'), icon: Coffee, color: 'bg-punch-break' });
      actions.push({ type: 'OUT', label: t('clockOut'), icon: LogOut, color: 'bg-punch-out' });
    }
    if (status === 'BREAK') {
      actions.push({ type: 'BREAK_END', label: t('breakReturn'), icon: RotateCcw, color: 'bg-punch-return' });
    }
    // Offline: show all actions since status may be stale
    if (!navigator.onLine && actions.length === 0) {
      actions.push({ type: 'IN', label: t('clockIn'), icon: LogIn, color: 'bg-punch-in' });
      actions.push({ type: 'OUT', label: t('clockOut'), icon: LogOut, color: 'bg-punch-out' });
    }
    return actions;
  };

  const handleActionSelect = (action) => {
    setSelectedAction(action);
    setStep('photo');
  };

  const capturePhoto = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (imageSrc) setPhoto(imageSrc);
    }
  }, [webcamRef]);

  // Punch: online → API, offline → queue
  const handleConfirmPunch = async () => {
    if (!photo) {
      toast.error(t('photoRequired'));
      return;
    }
    setLoading(true);
    const punchData = {
      tenantId,
      employeeCode: code,
      eventType: selectedAction.type,
      photoBase64: photo
    };

    try {
      if (navigator.onLine) {
        await axios.post(`${API}/punch`, punchData);
      } else {
        throw new Error('offline');
      }
      setStep('confirmation');
    } catch (error) {
      if (!navigator.onLine || (error.message === 'offline') || !error.response) {
        // Queue for later sync
        await queuePunch(punchData);
        setPendingCount((prev) => prev + 1);
        toast.info(language === 'fr'
          ? 'Pointage enregistre localement - sera synchronise au retour du reseau'
          : 'Punch saved locally - will sync when back online'
        );
        setStep('confirmation');
      } else {
        const message = error.response?.data?.detail || t('error');
        toast.error(message);
        if (message.includes('wait')) resetKiosk();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCameraError = () => setCameraError(true);

  // Online/Offline status badge
  const StatusBadge = () => (
    <div className="flex items-center gap-2">
      {!isOnline && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full" data-testid="offline-badge">
          <WifiOff size={14} className="text-amber-400" />
          <span className="text-xs font-medium text-amber-400">
            {language === 'fr' ? 'Hors ligne' : 'Offline'}
          </span>
        </div>
      )}
      {pendingCount > 0 && (
        <button
          onClick={syncQueue}
          disabled={syncing || !isOnline}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-500/20 border border-blue-500/30 rounded-full hover:bg-blue-500/30 transition-colors"
          data-testid="sync-badge"
        >
          <CloudUpload size={14} className={`text-blue-400 ${syncing ? 'animate-pulse' : ''}`} />
          <span className="text-xs font-medium text-blue-400">
            {pendingCount} {language === 'fr' ? 'en attente' : 'pending'}
          </span>
        </button>
      )}
      {isOnline && pendingCount === 0 && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 rounded-full" data-testid="online-badge">
          <Wifi size={14} className="text-green-400" />
          <span className="text-xs font-medium text-green-500">
            {language === 'fr' ? 'En ligne' : 'Online'}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="kiosk-mode min-h-screen flex flex-col font-body dark" data-testid="kiosk-home">
      <header className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo-punchtm.png" alt="PunchTM" className="h-10 w-auto" />
        </div>
        <div className="flex items-center gap-3">
          <StatusBadge />
          <button onClick={toggleLanguage} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="kiosk-language-toggle">
            <Globe size={18} />
            <span className="text-sm font-bold uppercase">{language}</span>
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-6">
        <AnimatePresence mode="wait">
          {step === 'loading' && (
            <motion.div key="loading" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="text-center">
              <RefreshCw className="animate-spin mx-auto text-white/50 mb-4" size={40} />
              <p className="text-white/60">{language === 'fr' ? 'Initialisation...' : 'Loading...'}</p>
            </motion.div>
          )}

          {step === 'setup' && (
            <motion.div key="setup" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="w-full max-w-md text-center" data-testid="kiosk-step-setup">
              <div className="w-16 h-16 bg-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Building2 className="text-accent" size={32} />
              </div>
              <h2 className="font-heading text-2xl font-bold text-white mb-2">
                {language === 'fr' ? 'Configuration du kiosque' : 'Kiosk Setup'}
              </h2>
              <p className="text-white/50 mb-8 text-sm">
                {language === 'fr' ? 'Entrez le code entreprise fourni par votre administrateur' : 'Enter the company code provided by your administrator'}
              </p>
              <div className="space-y-4">
                <input type="text" value={companyCode} onChange={(e) => { setCompanyCode(e.target.value.toUpperCase()); setSetupError(''); }} onKeyDown={(e) => e.key === 'Enter' && handleCompanyCodeSubmit()} placeholder={language === 'fr' ? 'Code entreprise (ex: ANTGEO)' : 'Company code (e.g. ANTGEO)'} className="w-full px-6 py-4 bg-slate-800 border-2 border-slate-700 rounded-xl text-white text-center text-2xl font-mono tracking-widest placeholder:text-slate-600 placeholder:text-base placeholder:tracking-normal focus:border-accent focus:outline-none" data-testid="company-code-input" autoFocus />
                {setupError && (
                  <div className="flex items-center justify-center gap-2 text-red-400 text-sm" data-testid="setup-error">
                    <AlertCircle size={16} /><span>{setupError}</span>
                  </div>
                )}
                <button onClick={handleCompanyCodeSubmit} disabled={!companyCode.trim() || loading} className="w-full py-4 text-lg bg-accent hover:bg-accent/90 text-white rounded-xl font-semibold disabled:opacity-50 transition-colors" data-testid="submit-company-code-btn">
                  {loading ? <RefreshCw className="animate-spin mx-auto" size={20} /> : (language === 'fr' ? 'Configurer' : 'Setup')}
                </button>
              </div>
            </motion.div>
          )}

          {step === 'code' && (
            <motion.div key="code" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="w-full max-w-md text-center" data-testid="kiosk-step-code">
              {tenantName && (
                <div className="mb-4 px-4 py-2 bg-slate-800/50 rounded-lg text-white/50 flex items-center gap-2 mx-auto text-sm w-fit" data-testid="tenant-badge">
                  <Building2 size={16} />{tenantName}
                </div>
              )}
              <h2 className="font-heading text-3xl font-bold text-white mb-8">{t('enterCode')}</h2>
              <div className="bg-slate-800 rounded-xl p-6 mb-6">
                <div className="font-mono text-5xl tracking-[0.5em] text-white h-16 flex items-center justify-center">
                  {code.padEnd(5, '_').split('').map((char, i) => (
                    <span key={i} className={char === '_' ? 'text-slate-600' : ''}>{char}</span>
                  ))}
                </div>
              </div>
              <div className="numpad-grid mb-6">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 'clear', 0, 'back'].map((key) => (
                  <button key={key} onClick={() => handleNumPadPress(key === 'clear' ? 'clear' : key === 'back' ? 'back' : key.toString())} className="numpad-btn" data-testid={`numpad-${key}`}>
                    {key === 'clear' ? 'C' : key === 'back' ? '\u2190' : key}
                  </button>
                ))}
              </div>
              <Button onClick={handleContinue} disabled={code.length !== 5 || loading || !tenantId} className="w-full h-16 text-xl font-bold bg-accent hover:bg-accent/90 text-white" data-testid="kiosk-continue-btn">
                {loading && <RefreshCw className="animate-spin mr-2" size={24} />}
                {t('continue')}
              </Button>
            </motion.div>
          )}

          {step === 'action' && employee && (
            <motion.div key="action" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="w-full max-w-xl text-center" data-testid="kiosk-step-action">
              <button onClick={resetKiosk} className="absolute top-20 left-6 p-3 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="kiosk-back-btn">
                <ArrowLeft size={24} />
              </button>
              <div className="mb-8">
                <div className="w-24 h-24 mx-auto mb-4 bg-slate-700 rounded-full flex items-center justify-center overflow-hidden">
                  {employee.photoProfileUrl ? <img src={employee.photoProfileUrl} alt="" className="w-full h-full object-cover" /> : <User size={48} className="text-slate-400" />}
                </div>
                <h2 className="font-heading text-3xl font-bold text-white">{employee.firstName} {employee.lastName}</h2>
                <p className="text-slate-400 mt-1">{employee.jobTitle}</p>
                <p className="text-slate-500 text-sm mt-1">{t('employeeCode')}: {employee.employeeCode}</p>
              </div>
              <h3 className="text-xl text-slate-300 mb-6">{t('chooseAction')}</h3>
              <div className="action-grid">
                {getAvailableActions().map((action) => (
                  <button key={action.type} onClick={() => handleActionSelect(action)} className={`${action.color} h-40 rounded-xl flex flex-col items-center justify-center gap-4 text-white font-bold text-xl transition-all hover:scale-[1.02] active:scale-[0.98]`} data-testid={`action-${action.type.toLowerCase()}`}>
                    <action.icon size={48} />{action.label}
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 'photo' && (
            <motion.div key="photo" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="w-full max-w-xl text-center" data-testid="kiosk-step-photo">
              <button onClick={() => { setStep('action'); setPhoto(null); }} className="absolute top-20 left-6 p-3 rounded-lg bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="kiosk-photo-back-btn">
                <ArrowLeft size={24} />
              </button>
              <h2 className="font-heading text-2xl font-bold text-white mb-6">{photo ? t('confirm') : t('takePhoto')}</h2>
              <div className="relative aspect-[4/3] bg-slate-800 rounded-xl overflow-hidden mb-6">
                {cameraError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white">
                    <AlertCircle size={48} className="text-red-400 mb-4" />
                    <p className="text-lg font-medium">{t('cameraAccess')}</p>
                    <p className="text-slate-400 text-sm mt-2">{t('enableCamera')}</p>
                  </div>
                ) : photo ? (
                  <img src={photo} alt="Captured" className="w-full h-full object-cover" />
                ) : (
                  <>
                    <Webcam ref={webcamRef} audio={false} screenshotFormat="image/jpeg" screenshotQuality={0.8} videoConstraints={{ facingMode: "user", width: 640, height: 480 }} onUserMediaError={handleCameraError} className="w-full h-full object-cover" />
                    <div className="photo-guide-oval" />
                  </>
                )}
              </div>
              <div className="flex gap-4">
                {photo ? (
                  <>
                    <Button onClick={() => setPhoto(null)} variant="outline" className="flex-1 h-16 text-lg font-bold border-white/30 text-white hover:bg-white/10" data-testid="kiosk-retake-btn">
                      <RefreshCw className="mr-2" size={24} />{t('retake')}
                    </Button>
                    <Button onClick={handleConfirmPunch} disabled={loading} className="flex-1 h-16 text-lg font-bold bg-success hover:bg-success/90 text-white" data-testid="kiosk-confirm-btn">
                      {loading ? <RefreshCw className="animate-spin mr-2" size={24} /> : <Check className="mr-2" size={24} />}
                      {t('confirm')}
                    </Button>
                  </>
                ) : (
                  <Button onClick={capturePhoto} disabled={cameraError} className="w-full h-16 text-xl font-bold bg-accent hover:bg-accent/90 text-white" data-testid="kiosk-capture-btn">
                    <Camera className="mr-2" size={24} />{t('takePhoto')}
                  </Button>
                )}
              </div>
            </motion.div>
          )}

          {step === 'confirmation' && (
            <motion.div key="confirmation" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.3 }} className="w-full max-w-md text-center" data-testid="kiosk-step-confirmation">
              <div className="w-24 h-24 mx-auto mb-6 bg-success rounded-full flex items-center justify-center animate-pulse-green">
                <Check size={48} className="text-white" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-white mb-4">{t('punchRecorded')}</h2>
              <div className="bg-slate-800 rounded-xl p-6 mb-6">
                <p className="text-xl text-white font-medium">{employee?.firstName} {employee?.lastName}</p>
                <p className={`text-2xl font-bold mt-2 ${selectedAction?.type === 'IN' ? 'text-punch-in' : selectedAction?.type === 'BREAK_START' ? 'text-punch-break' : selectedAction?.type === 'BREAK_END' ? 'text-punch-return' : 'text-punch-out'}`}>
                  {selectedAction?.label}
                </p>
                <p className="text-slate-400 mt-2">
                  {new Date().toLocaleTimeString(language === 'fr' ? 'fr-FR' : 'en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
                </p>
                {!isOnline && (
                  <p className="text-amber-400 text-sm mt-3 flex items-center justify-center gap-1">
                    <WifiOff size={14} />
                    {language === 'fr' ? 'Enregistre localement - synchronisation au retour du reseau' : 'Saved locally - will sync when online'}
                  </p>
                )}
              </div>
              <p className="text-slate-400">
                {t('returningIn')} <span className="text-white font-bold">{countdown}</span> {t('seconds')}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="p-4 text-center text-sm" data-testid="kiosk-footer">
        <p className="text-slate-500">&copy; {new Date().getFullYear()} PunchTM - Propulse par TM Consulting et Formation</p>
        <a href="/admin/login" className="text-slate-600 hover:text-slate-400 transition-colors text-xs mt-1 inline-block" data-testid="admin-link">Administration</a>
      </footer>
    </div>
  );
}
